import Footer from '@/components/Footer';
import { ChangeBookingFooterContent } from '@/components/manage/change-trips/ChangeBookingFooterContent';
import { MapModal } from '@/components/map-component/MapModal';
import { JourneysNotFound } from '@/components/purchase/journey/JourneysNotFound';
import { NonTripOffersNotFound } from '@/components/purchase/journey/NonTripOffersNotFound';
import { SearchWidgetWrapper } from '@/components/search-widget';
import { resetChangeTripsSelection } from '@/features/manage/manageActions';
import {
  inboundDataSelector,
  nonTripOffersSelector,
  outboundDataSelector,
  purchaseFlowBookingSelector,
} from '@/features/purchase/purchaseSelectors';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useTenantConfiguration } from '@/hooks/useTenantConfiguration';
import { TransText } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { breakpoints } from '@/utils/breakpoints';
import { shouldShowPurchaseSearchWidget } from '@/utils/purchase';
import { Button, Icons } from '@packages/webshop-design-system';
import { type FC, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';

interface PurchaseProps {}

const Purchase: FC<PurchaseProps> = () => {
  const dispatch = useDispatch();
  const purchaseFlowBooking = useSelector(purchaseFlowBookingSelector);
  const outboundData = useSelector(outboundDataSelector);
  const inboundData = useSelector(inboundDataSelector);
  const nonTripOffersData = useSelector(nonTripOffersSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const { changeFlow, hasSelection } = useChangeBookingSelection();
  const tenantConfig = useTenantConfiguration();
  const enableMapComponent = !!tenantConfig?.enableMapComponent;
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });
  // /purchase is the only step in the change-booking flow that doesn't
  // own its own Footer (the per-step JourneyList / JourneyOfferList
  // routes render their own). Mount one here so the user can always
  // reach /manage-booking from the initial search page.
  const showChangeBookingFooter =
    hasSelection && location.pathname === '/purchase';

  usePageTitle('purchase');

  useEffect(() => {
    // A full booking (more than the rehydrated `{ id }` stub) means there's an
    // in-progress checkout to resume — send the user there. The stub itself is
    // fetched and expiry-validated globally by BookingExpiryProvider, so we
    // only react once the full booking lands in the store.
    if (Object.keys(purchaseFlowBooking ?? {}).length > 1) {
      navigate('/purchase/checkout');
    }
  }, [navigate, purchaseFlowBooking]);

  return (
    <PersistGate loading={null} persistor={persistor}>
      <>
        {shouldShowPurchaseSearchWidget(location.pathname, changeFlow) && (
          <SearchWidgetWrapper />
        )}
        <Outlet />
        <JourneysNotFound
          outboundData={outboundData}
          inboundData={inboundData}
        />
        <NonTripOffersNotFound {...nonTripOffersData} />
        {showChangeBookingFooter && (
          <Footer
            content={<ChangeBookingFooterContent />}
            actionButtons={
              <Button
                size={isLaptopOrBigger ? 'medium' : 'large'}
                data-testid="footer-back-button"
                className="rounded-lg laptop:w-auto"
                variant="tertiary"
                onClick={() => {
                  // Backing out of the exchange search abandons the change
                  // flow — drop the persisted selection so /purchase and the
                  // booking summary don't stay locked in change mode.
                  dispatch(resetChangeTripsSelection());
                  navigate('/manage-booking');
                }}
              >
                <Icons.arrowLeft />
                <TransText i18nKey="back" />
              </Button>
            }
          />
        )}
        {enableMapComponent && <MapModal />}
      </>
    </PersistGate>
  );
};

export default Purchase;
